import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {faAlignLeft, faCopy, faDownload, faTrashAlt} from '@fortawesome/free-solid-svg-icons';

import ToolBar from '../../../../UI/ToolBar/ToolBar';
import Buttons from '../../../../UI/ToolBar/Buttons';
import useLocalStorage from '../../../../Hooks/useLocalStorage';
import CodeEditor from '../../../../UI/CodeEditor/CodeEditor';
import styles from './SplitConverter.module.scss';

const INPUT_STORAGE_KEY = 'converter-input-values'

function safeConvert(convert, input, options) {
    if (!input.trim()) return {output: '', error: ''}
    try {
        return {output: convert(input, options), error: ''}
    } catch (err) {
        return {output: '', error: err.message || 'Conversion failed'}
    }
}

function parseStoredInputs(raw) {
    try {
        return JSON.parse(raw) || {}
    } catch (err) {
        return {}
    }
}

const targetShape = PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    convert: PropTypes.func.isRequired,
    extension: PropTypes.string.isRequired,
    mimeType: PropTypes.string.isRequired,
    formattable: PropTypes.bool,
})

SplitConverter.propTypes = {
    sources: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        targets: PropTypes.arrayOf(targetShape).isRequired,
    })).isRequired,
}

export default function SplitConverter({sources}) {
    let [storedInputs, setStoredInputs] = useLocalStorage(INPUT_STORAGE_KEY)
    let [sourceKey, setSourceKey] = useState(sources[0].key)
    let [targetKey, setTargetKey] = useState(sources[0].targets[0].key)
    let [copied, setCopied] = useState(false)
    let [compact, setCompact] = useState(false)

    const inputsBySource = parseStoredInputs(storedInputs)
    const inputValue = inputsBySource[sourceKey] || ''

    const source = sources.find(s => s.key === sourceKey) || sources[0]
    const target = source.targets.find(t => t.key === targetKey) || source.targets[0]
    const {output, error} = useMemo(() => safeConvert(target.convert, inputValue, {compact}), [target, inputValue, compact])

    const handleInputChange = (value) => {
        setStoredInputs(JSON.stringify({...inputsBySource, [sourceKey]: value}))
    }

    const handleSourceChange = (key) => {
        setSourceKey(key)
        const newSource = sources.find(s => s.key === key)
        if (!newSource.targets.some(t => t.key === targetKey)) {
            setTargetKey(newSource.targets[0].key)
        }
    }

    const handleCopy = () => {
        if (!output) return
        navigator.clipboard.writeText(output)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    const handleDownload = () => {
        if (!output) return
        const blob = new Blob([output], {type: target.mimeType})
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `converted.${target.extension}`
        link.click()
        URL.revokeObjectURL(url)
    }

    const handleResetInput = () => handleInputChange('')

    const handleLoadSample = () => handleInputChange(source.placeholder)

    const sourceButtons = sources.map(s => ({
        tip: s.label,
        text: s.label,
        clickHandler: () => handleSourceChange(s.key),
        active: s.key === source.key,
    }))

    const targetButtons = source.targets.map(t => ({
        tip: t.label,
        text: t.label,
        clickHandler: () => setTargetKey(t.key),
        active: t.key === target.key,
    }))

    const inputActionButtons = [
        {
            tip: 'Load sample data',
            clickHandler: handleLoadSample,
            text: 'Sample',
        },
        {
            tip: 'Reset input',
            clickHandler: handleResetInput,
            icon: faTrashAlt,
        },
    ]

    const formatOptionButtons = target.formattable ? [{
        tip: compact ? 'Switch to pretty output' : 'Switch to compact output',
        clickHandler: () => setCompact(c => !c),
        icon: faAlignLeft,
        active: compact,
    }] : []

    const outputActionButtons = [
        {
            tip: copied ? 'Copied!' : 'Copy to clipboard',
            clickHandler: handleCopy,
            icon: faCopy,
        },
        {
            tip: 'Download file',
            clickHandler: handleDownload,
            icon: faDownload,
        },
    ]

    return (
        <div className={styles.main}>
            <div className={styles.editor}>
                <ToolBar>
                    <div className={styles.paneToolbar}>
                        <Buttons buttons={inputActionButtons}/>
                        <Buttons buttons={sourceButtons}/>
                    </div>
                </ToolBar>
                <CodeEditor language={source.key} value={inputValue} onChange={handleInputChange}/>
            </div>
            <div className={styles.result}>
                <ToolBar>
                    <div className={`${styles.paneToolbar} ${styles.resultToolbar}`}>
                        <Buttons  buttons={targetButtons}/>
                        <div className={styles.paneToolbar} style={{justifyContent:"flex-end"}}>
                            <Buttons buttons={formatOptionButtons}/>
                            <Buttons buttons={outputActionButtons}/>
                        </div>

                    </div>
                </ToolBar>
                {error && <div className={styles.error}>{error}</div>}
                <CodeEditor language={target.key} value={output} readOnly/>
            </div>
        </div>
    );
}
